import {
  Ad,
  BatchEventType,
  MediaSubtype,
  Stream,
  Subtitle,
} from '@accedo/one-plugin-sdk';
import { MediaType } from '@dynamic/ui';

import { Item } from '#/client/models';

// we use the same type for `playerFeatures` and `audioPlayerFeatures` as long as they can stay compatible.
// all fields should be optional, because they are optional in the Core response, and also because some might
// not apply to both audio and video.
/** Object with player feature flags received from Core. */
export interface PlayerFeatures {
  // this is added in `parsePlaybackInfoResponse` just so we can easily check which set of player features are we using.
  _debug?: 'audio' | 'video';
  trickPlay?: boolean;
  audioTracks?: {
    namingBehavior: TrackNamingBehavior;
  };
  subtitleTracks?: {
    namingBehavior: TrackNamingBehavior;
  };
  errorHandling?: {
    restartPlaybackOnAnyError?: {
      enabled: boolean;
      retryCount?: number;
      retryInterval?: string;
    };
  };
}

export enum TrackNamingBehavior {
  UseAsProvided = 'USE_AS_PROVIDED',
  Rewrite = 'REWRITE',
}

// has `videoStreams` or `audioStreams`, and `playerFeatures` or `audioPlayerFeatures` depending on the item's `subtype`. see `isAudio`.
// we should always decide which to use based on subtype, so even if the responses have both, we use the correct one.
export interface PlaybackInfo {
  videoStreams?: Stream[];
  audioStreams?: Stream[];
  advertisements: Ad[];
  audioTracks: {};
  subtitles: Subtitle[];
  // these two are coming back separately from Core, we only return the correct one in `parsePlaybackInfoResponse`
  // as `playerFeatures` so the rest of the app can just use that (via `PlaybackDetails` type).
  playerFeatures?: PlayerFeatures;
  audioPlayerFeatures?: PlayerFeatures;
}

export enum MarkerCueType {
  INTRO = 'INTRO',
  RECAP = 'RECAP',
  CREDITS = 'CREDITS',
  FULLY_WATCHED = 'FULLY_WATCHED',
}

export interface MarkerCue {
  // `duration` is unused.
  endMs: number;
  skippable: boolean;
  startMs: number;
  type: MarkerCueType;
}

export interface Markers {
  end?: number;
  fullyWatched?: number;
  cues?: MarkerCue[];
}

export interface PlaybackInfoRefs {
  interrupt: string;
  resume: string;
  next: string;
}

/**
 * Payload for content.playback.progress events
 * Matches the structure from /play response events["content.playback.progress"]
 */
export interface PlaybackProgressEventPayload {
  objectId?: string;
  objectType?: MediaType;
  objectSubtype?: MediaSubtype;
  sessionGUID?: string;
  locale?: string;
  via?: string;
  fullyWatched?: string;
  excludeFromWatchHistory?: boolean;
  playbackRequestType?: string;
  offline?: boolean;
  progress: string;
  entitlementTags?: string[];
}

export interface PlaybackInfoResponse {
  playbackInfo: PlaybackInfo;
  watched?: {
    progress?: {
      seconds?: number;
      finished?: boolean;
    };
  };
  markers?: Markers;
  refs: PlaybackInfoRefs;
  included?: {
    contentObject?: Item;
  };
  sessionGuid: string;
  events?: {
    [BatchEventType.PlaybackProgress]?: PlaybackProgressEventPayload;
  };
}
